import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, ComposedChart, Area,
} from "recharts";
import { TrendingUp, Users, Building2, UserCheck, XCircle, ArrowUpRight, ArrowDownRight, Minus, CalendarDays } from "lucide-react";

const BAR_COLORS = ["#1e293b", "#dc2626", "#2563eb", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#6366f1"];

function shortWeek(dateStr) {
  try {
    const d = new Date(dateStr + "T12:00:00");
    return `${d.getDate()} ${d.toLocaleDateString("es", { month: "short" })}`;
  } catch { return dateStr; }
}

function shortMonth(dateStr) {
  try {
    const [y, m] = dateStr.split("-");
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return d.toLocaleDateString("es", { month: "short", year: "2-digit" });
  } catch { return dateStr; }
}

function ChangeIndicator({ value, inverse = false }) {
  if (value === 0 || value === null || value === undefined) return null;
  const positive = value > 0;
  const color = inverse ? (positive ? "text-red-500" : "text-green-500") : (positive ? "text-green-500" : "text-red-500");
  return (
    <span className={`text-xs font-bold flex items-center gap-0.5 ${color}`}>
      {positive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {Math.abs(value)}%
    </span>
  );
}

export default function AnalyticsView({ period, setPeriod }) {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosAuth().get(`/analytics/charts?period=${period}`).then(r => {
      setData(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [period, axiosAuth]);

  if (loading) return <div className="text-center py-12 text-slate-400 text-sm">Cargando analitica...</div>;
  if (!data) return <div className="text-center py-12 text-slate-400 text-sm">Sin datos disponibles</div>;

  const comp = data.comparison || {};

  const clientsPieData = [
    { name: "Nuevos", value: data.clients.new, fill: "#2563eb" },
    { name: "Recurrentes", value: data.clients.recurrent, fill: "#16a34a" },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-4" data-testid="analytics-view">
      {/* Period info */}
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
        {data.period.start} a {data.period.end} &middot; {data.period.total} citas
      </p>

      {/* Comparison KPI row */}
      <div className="grid grid-cols-4 gap-3" data-testid="analytics-comparison-kpis">
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{comp.total?.current ?? data.period.total}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Citas totales</p>
              </div>
              <ChangeIndicator value={comp.total?.change} />
            </div>
            {comp.total && <p className="text-[9px] text-slate-400 mt-1">Anterior: {comp.total.previous}</p>}
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{comp.confirmadas?.current ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Confirmadas</p>
              </div>
              <ChangeIndicator value={comp.confirmadas?.change} />
            </div>
            {comp.confirmadas && <p className="text-[9px] text-slate-400 mt-1">Anterior: {comp.confirmadas.previous}</p>}
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{data.cancel_rate}%</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tasa cancelacion</p>
              </div>
              <ChangeIndicator value={comp.canceladas?.change} inverse />
            </div>
            {comp.canceladas && <p className="text-[9px] text-slate-400 mt-1">{comp.canceladas.current} canceladas (ant: {comp.canceladas.previous})</p>}
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{data.clients.new + data.clients.recurrent}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Clientes unicos</p>
              </div>
              <div className="text-right">
                <span className="text-[9px] text-blue-600 font-semibold">{data.clients.new} nuevos</span>
                <br />
                <span className="text-[9px] text-green-600 font-semibold">{data.clients.recurrent} recur.</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison label */}
      {comp.label && <p className="text-[10px] text-slate-400 uppercase tracking-wider text-right -mt-2" data-testid="analytics-compare-label">{comp.label}</p>}

      {/* Charts grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* 1. Citas por semana */}
        {data.weekly.length > 0 && (
          <Card className="border-slate-200 rounded-sm col-span-2">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-red-500" /> Evolucion semanal
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.weekly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="week" tickFormatter={shortWeek} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} labelFormatter={shortWeek} />
                  <Line type="monotone" dataKey="total" stroke="#1e293b" strokeWidth={2.5} dot={{ r: 4 }} name="Total" />
                  <Line type="monotone" dataKey="confirmadas" stroke="#2563eb" strokeWidth={1.5} dot={{ r: 3 }} name="Confirmadas" />
                  <Line type="monotone" dataKey="canceladas" stroke="#dc2626" strokeWidth={1.5} dot={{ r: 3 }} name="Canceladas" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* 2. Clientes nuevos vs recurrentes */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-blue-500" /> Clientes nuevos vs recurrentes
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {clientsPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={clientsPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {clientsPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-slate-400 py-8">Sin datos de clientes</p>
            )}
          </CardContent>
        </Card>

        {/* 3. Citas por centro */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-purple-500" /> Citas por centro
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {data.by_center.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.by_center} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                  <Bar dataKey="total" name="Citas" radius={[0, 3, 3, 0]}>
                    {data.by_center.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-slate-400 py-8">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* 4. Citas por asesor */}
        <Card className="border-slate-200 rounded-sm col-span-2">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5 text-green-500" /> Carga por asesor
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {data.by_advisor.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(160, data.by_advisor.length * 32)}>
                <BarChart data={data.by_advisor} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                  <Bar dataKey="total" name="Citas" radius={[0, 3, 3, 0]}>
                    {data.by_advisor.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-sm text-slate-400 py-8">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* 5. Panel comparativa mensual */}
        <Card className="border-slate-200 rounded-sm col-span-2">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5 text-red-500" /> Evolucion mensual
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {data.monthly_evolution?.length > 0 ? (
              <>
                {/* Monthly summary badges */}
                <div className="flex flex-wrap gap-2 mb-3 px-2">
                  {data.monthly_evolution.slice(-3).map((m, i) => (
                    <div key={m.month} className="flex items-center gap-2 bg-slate-50 rounded-sm px-3 py-1.5 border border-slate-100">
                      <span className="text-[10px] font-semibold text-slate-700 uppercase">{shortMonth(m.month)}</span>
                      <span className="text-sm font-bold text-slate-900">{m.total}</span>
                      {m.change_pct !== 0 && i > 0 && (
                        <span className={`text-[10px] font-bold flex items-center gap-0.5 ${m.change_pct > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {m.change_pct > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {Math.abs(m.change_pct)}%
                        </span>
                      )}
                      <span className={`text-[9px] ${m.cancel_rate > 10 ? 'text-red-500' : 'text-slate-400'}`}>{m.cancel_rate}% canc.</span>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={data.monthly_evolution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} labelFormatter={shortMonth} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar yAxisId="left" dataKey="total" name="Total" fill="#1e293b" radius={[3, 3, 0, 0]} barSize={24} />
                    <Bar yAxisId="left" dataKey="confirmadas" name="Confirmadas" fill="#2563eb" radius={[3, 3, 0, 0]} barSize={24} />
                    <Line yAxisId="right" type="monotone" dataKey="cancel_rate" name="% Cancelacion" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-sm text-slate-400 py-8">Sin datos mensuales</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
